import { Chip, darken, styled, type Theme } from '@mui/material'

export type StatusIntent = 'success' | 'warning' | 'danger' | 'info' | 'neutral'

interface IntentColors {
	background: string
	text: string
	border: string
}

const getIntentColors = (theme: Theme, intent: StatusIntent): IntentColors => {
	switch (intent) {
		case 'success':
			return {
				background: theme.palette.backgroundColors.greenLight,
				text: theme.palette.elementColors.good,
				border: theme.palette.elementColors.good,
			}
		case 'warning':
			return {
				background: theme.palette.backgroundColors.warningLight,
				text: theme.palette.elementColors.warningHeavy,
				border: theme.palette.elementColors.warningHeavy,
			}
		case 'danger':
			return {
				background: theme.palette.backgroundColors.errorLight,
				text: theme.palette.elementColors.bad,
				border: theme.palette.elementColors.bad,
			}
		case 'info':
			return {
				background: theme.palette.backgroundColors.infoLight,
				text: theme.palette.elementColors.info,
				border: theme.palette.elementColors.info,
			}
		case 'neutral':
			return {
				background: theme.palette.backgroundColors.grey,
				text: theme.palette.fonts.secondary,
				border: theme.palette.borderColors.darkGray,
			}
	}
}

const StyledChip = styled(Chip, {
	shouldForwardProp: prop => prop !== 'intent',
})<{ intent: StatusIntent }>(({ theme, intent }) => {
	const colors = getIntentColors(theme, intent)
	return {
		backgroundColor: colors.background,
		color: colors.text,
		border: `1px solid ${colors.border}`,
		fontWeight: 500,
		margin: 0,
		'& .MuiChip-label': {
			color: colors.text,
		},
		'&.MuiChip-clickable:hover': {
			backgroundColor: darken(colors.background, 0.05),
		},
	}
})

interface StatusChipProps {
	intent: StatusIntent
	label: string
	onClick?: () => void
	size?: 'small' | 'medium'
}

const StatusChip: React.FC<StatusChipProps> = ({
	intent,
	label,
	onClick,
	size = 'small',
}) => (
	<StyledChip
		intent={intent}
		label={label}
		size={size}
		onClick={onClick}
		clickable={Boolean(onClick)}
		data-intent={intent}
	/>
)

export { StatusChip }
